// hooks/useInfiniteScroll.js
import { useState, useEffect, useCallback, useRef } from 'react';

const useInfiniteScroll = ({
  hasMore = false,
  loadMore,
  threshold = 200, // Distance from bottom to trigger load
  enabled = true
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const loadingRef = useRef(null);

  // Intersection Observer for detecting when user scrolls near bottom
  useEffect(() => {
    if (!enabled || !hasMore || isFetching) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isFetching) {
          setIsFetching(true);
        }
      },
      {
        rootMargin: `${threshold}px 0px`,
        threshold: 0.1
      }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => observer.disconnect();
  }, [enabled, hasMore, isFetching, threshold]);

  // Handle loading more data
  useEffect(() => {
    if (!isFetching || !hasMore) return;

    const handleLoadMore = async () => {
      try {
        if (loadMore) {
          await loadMore();
        }
      } catch (error) {
        console.error('Error loading more data:', error);
      } finally {
        setIsFetching(false);
      }
    };

    handleLoadMore();
  }, [isFetching, hasMore, loadMore]);

  // Manual trigger for loading more
  const triggerLoadMore = useCallback(() => {
    if (hasMore && !isFetching && enabled) {
      setIsFetching(true);
    }
  }, [hasMore, isFetching, enabled]);

  return {
    isFetching,
    loadingRef,
    triggerLoadMore
  };
};

export default useInfiniteScroll;